// GameInitializationCoordinator.js
// Enterprise-grade game initialization coordinator
// Fixes camera dimension invalidation and timing issues

import { MobileDetectionService } from './MobileDetectionService.js';
import { professionalNotifications } from './ProfessionalNotificationSystem.js';
import CSSConflictResolver from './CSSConflictResolver.js';

/**
 * Game Initialization Coordinator
 * Manages proper sequence of game initialization to prevent camera dimension issues
 * Coordinates between enterprise services and Phaser game engine
 */
export class GameInitializationCoordinator {
    static _instance = null;
    
    /**
     * Singleton pattern for consistent game initialization
     */
    static getInstance() {
        if (!GameInitializationCoordinator._instance) {
            GameInitializationCoordinator._instance = new GameInitializationCoordinator();
        }
        return GameInitializationCoordinator._instance;
    }
    
    constructor() {
        if (GameInitializationCoordinator._instance) {
            return GameInitializationCoordinator._instance;
        }
        
        this.initializingGames = new Set();
        this.gameStates = new Map();
        
        GameInitializationCoordinator._instance = this;
    }
    
    /**
     * Initialize game with proper sequence to prevent camera dimension issues
     * @param {BaseMenuScene} fromScene - Source scene
     * @param {string} gameSceneKey - Target game scene
     * @returns {Promise<boolean>} - Success status
     */
    async initializeGame(fromScene, gameSceneKey) {
        if (this.initializingGames.has(gameSceneKey)) {
            console.warn(`🎮 Game ${gameSceneKey} already initializing`);
            return false;
        }
        
        console.log(`🚀 GameInitializationCoordinator: Initializing ${gameSceneKey}`);
        this.initializingGames.add(gameSceneKey);
        
        try {
            // Step 1: Ensure CSS conflicts are resolved
            if (!CSSConflictResolver.areConflictsResolved()) {
                CSSConflictResolver.resolveAllConflicts();
            }
            
            // Step 2: Prepare DOM environment for game
            await this._prepareDOMEnvironment(gameSceneKey);
            
            // Step 3: Clean up interfering elements
            await this._cleanupInterferingElements();
            
            // Step 4: Set game active state
            this._setGameActiveState(true);
            
            // Step 5: Initialize game with proper timing
            const success = await this._initializeGameWithTiming(fromScene, gameSceneKey);
            
            // Step 6: Validate game initialization
            if (success) {
                await this._validateGameInitialization(gameSceneKey);
            }
            
            return success;
            
        } catch (error) {
            console.error(`🚨 Game initialization failed for ${gameSceneKey}:`, error);
            this._setGameActiveState(false);
            return false;
        } finally {
            this.initializingGames.delete(gameSceneKey);
        }
    }
    
    /**
     * Cleanup when returning from game to DOM scenes
     * @param {string} gameSceneKey - Game scene being exited
     */
    cleanupGameState(gameSceneKey) {
        console.log(`🧹 GameInitializationCoordinator: Cleaning up ${gameSceneKey}`);
        
        // Remove game active state
        this._setGameActiveState(false);
        
        // Clean up game-specific modifications
        this._restoreDOMSceneEnvironment();
        
        // Remove from tracking
        this.gameStates.delete(gameSceneKey);
        
        console.log(`✅ Game state cleanup complete for ${gameSceneKey}`);
    }
    
    /**
     * Prepare DOM environment for game initialization
     * @private
     */
    async _prepareDOMEnvironment(gameSceneKey) {
        console.log('🔧 Preparing DOM environment for game...');
        
        // Remove ALL DOM overlays that could interfere
        const overlays = document.querySelectorAll('[id*="overlay"]:not(.notification-container), [id="auth-menu-dom-overlay"]');
        overlays.forEach(overlay => {
            overlay.remove();
            console.log(`🗑️ Removed interfering overlay: ${overlay.id}`);
        });
        
        // CRITICAL: Also remove dashboard sidebar and app container specifically
        const sidebar = document.querySelector('.sidebar');
        if (sidebar) {
            console.log('🗑️ Removed dashboard sidebar');
            sidebar.remove();
        }
        
        const appContainer = document.querySelector('.app-container');
        if (appContainer) {
            console.log('🗑️ Removed dashboard app-container');
            appContainer.remove();
        }
        
        // CRITICAL: Remove any nav-menu elements specifically
        const navMenus = document.querySelectorAll('.nav-menu, .nav-section, .nav-item');
        navMenus.forEach(nav => {
            console.log(`🗑️ Removed navigation element: ${nav.className}`);
            nav.remove();
        });
        
        // CRITICAL: Remove background effects from dashboard
        const backgroundEffects = document.querySelector('.background-effects');
        if (backgroundEffects) {
            console.log('🗑️ Removed dashboard background effects');
            backgroundEffects.remove();
        }
        
        // CRITICAL: Add CSS to hide any remaining navigation elements
        const hideNavCSS = document.createElement('style');
        hideNavCSS.id = 'hide-dashboard-nav';
        hideNavCSS.textContent = `
            #auth-menu-dom-overlay,
            .sidebar, 
            .nav-menu, 
            .nav-section, 
            .nav-item, 
            .app-container, 
            .background-effects,
            .sidebar-header,
            .sidebar-footer,
            .logo,
            .logo-text,
            .logo-title,
            .logo-subtitle,
            .nav-section-title,
            .nav-icon,
            .nav-label,
            .nav-badge,
            .user-profile,
            .user-avatar,
            .user-info,
            .user-name,
            .user-role {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
                pointer-events: none !important;
                width: 0 !important;
                height: 0 !important;
                overflow: hidden !important;
            }
            
            /* Force hide the entire overlay */
            [id*="auth-menu-dom-overlay"] {
                display: none !important;
                visibility: hidden !important;
                z-index: -9999 !important;
            }
        `;
        document.head.appendChild(hideNavCSS);
        console.log('🚫 Added aggressive CSS to hide all dashboard navigation elements');
        
        // CRITICAL: Continuously monitor and remove dashboard elements
        const dashboardMonitor = setInterval(() => {
            const dashboardOverlay = document.getElementById('auth-menu-dom-overlay');
            if (dashboardOverlay) {
                console.log('🗑️ Detected dashboard overlay recreation - removing again');
                dashboardOverlay.remove();
            }
            
            const sidebar = document.querySelector('.sidebar');
            if (sidebar && sidebar.offsetWidth > 0) {
                console.log('🗑️ Detected visible sidebar - force hiding');
                sidebar.style.display = 'none !important';
                sidebar.remove();
            }
        }, 100); // Check every 100ms
        
        // Stop monitoring after 10 seconds (game should be loaded by then)
        setTimeout(() => {
            clearInterval(dashboardMonitor);
            console.log('🔍 Stopped dashboard monitoring');
        }, 10000);
        
        // Ensure game container has proper base positioning
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
        // CRITICAL: Ensure proper dimensions for Phaser camera
        // Remove all CSS classes that might interfere
        gameContainer.classList.remove('authenticated-dashboard');
        document.body.classList.remove('authenticated-dashboard-active');
        
        // CRITICAL FIX: Force proper positioning for camera dimension calculation
        gameContainer.style.position = 'absolute';
        gameContainer.style.top = '50px';
        gameContainer.style.left = '0';
        gameContainer.style.width = '100%';
        gameContainer.style.height = 'calc(100% - 50px)';
        gameContainer.style.display = 'block';
        gameContainer.style.visibility = 'visible';
        gameContainer.style.opacity = '1';
        
        console.log('✅ Game container prepared with proper dimensions for camera');
        }
        
        // Ensure top ribbon is ready (PPT Hero dependency)
        const topRibbon = document.getElementById('top-ribbon');
        if (topRibbon) {
            topRibbon.style.display = 'flex';
            topRibbon.style.visibility = 'visible';
            topRibbon.style.opacity = '1';
            topRibbon.style.zIndex = '100';
            topRibbon.style.position = 'fixed';
            topRibbon.style.top = '0';
            topRibbon.style.left = '0';
            topRibbon.style.width = '100%';
            topRibbon.style.height = '50px';
            console.log('✅ Top ribbon prepared and positioned for navigation back to main menu');
        }
    }
    
    /**
     * Clean up elements that interfere with game rendering
     * @private
     */
    async _cleanupInterferingElements() {
        console.log('🧹 Cleaning up interfering elements...');
        
        // Hide persistent loading elements
        const loadingElements = document.querySelectorAll('#loadingStatus, .status-text, .loading-state');
        loadingElements.forEach(element => {
            element.style.display = 'none';
            console.log(`🙈 Hidden loading element: ${element.id || element.className}`);
        });
        
        // Hide preloader if still visible
        const preloader = document.getElementById('preloader');
        if (preloader && getComputedStyle(preloader).display !== 'none') {
            preloader.style.display = 'none';
            console.log('🙈 Hidden preloader');
        }
        
        // Clean up any stuck notifications that might interfere
        const stuckNotifications = document.querySelectorAll('.professional-notification');
        stuckNotifications.forEach(notification => {
            if (notification.id !== 'game-loading-notification') {
                notification.remove();
                console.log(`🗑️ Removed stuck notification: ${notification.id}`);
            }
        });
    }
    
    /**
     * Set game active state with proper CSS classes
     * @private
     */
    _setGameActiveState(active) {
        if (active) {
            document.body.classList.add('game-active');
            document.body.classList.remove('authenticated-dashboard-active');
            console.log('🎮 Game active state enabled');
        } else {
            document.body.classList.remove('game-active');
            console.log('🖥️ Game active state disabled');
        }
    }
    
    /**
     * Initialize game with proper timing to prevent camera issues
     * @private
     */
    async _initializeGameWithTiming(fromScene, gameSceneKey) {
        console.log('⏰ Initializing game with proper timing...');
        
        // Show loading notification
        const loadingId = professionalNotifications.loading(
            `Initializing ${gameSceneKey.replace('Scene', '').replace('Generic', '')}...`,
            { duration: 0, persistent: true }
        );
        
        // CRITICAL: Let Phaser handle game container positioning
        // Don't override until after camera is ready
        
        try {
            // Use SceneManager for transition (it handles timing properly)
            if (fromScene.startScene) {
                fromScene.startScene(gameSceneKey);
            } else {
                fromScene.scene.start(gameSceneKey);
            }
            
            // Wait for game to signal it's ready
            await this._waitForGameReady(gameSceneKey, 5000);
            
            // Dismiss loading notification
            professionalNotifications.dismiss(loadingId);
            
            return true;
            
        } catch (error) {
            console.error('🚨 Game initialization timing failed:', error);
            professionalNotifications.dismiss(loadingId);
            return false;
        }
    }
    
    /**
     * Wait for game to be ready with valid camera dimensions
     * @private
     */
    async _waitForGameReady(gameSceneKey, timeout = 5000) {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();
            let attempts = 0;
            const maxAttempts = 50;
            
            const checkReady = () => {
                attempts++;
                
                if (Date.now() - startTime > timeout) {
                    reject(new Error(`Game initialization timeout: ${gameSceneKey}`));
                    return;
                }
                
                const gameScene = window.game?.scene?.getScene(gameSceneKey);
                if (!gameScene) {
                    setTimeout(checkReady, 100);
                    return;
                }
                
                const isActive = gameScene.scene.isActive();
                const hasCamera = gameScene.cameras && gameScene.cameras.main;
                const hasValidDimensions = hasCamera && 
                    gameScene.cameras.main.width > 0 && 
                    gameScene.cameras.main.height > 0;
                
                console.log(`🔍 Game ready check ${attempts}: Active=${isActive}, Camera=${hasCamera}, ValidDims=${hasValidDimensions}`);
                
                if (isActive && hasCamera && hasValidDimensions) {
                    console.log(`✅ Game ${gameSceneKey} ready with valid camera dimensions`);
                    resolve(gameScene);
                } else if (attempts >= maxAttempts) {
                    console.warn(`⚠️ Game ${gameSceneKey} ready but camera dimensions may be invalid`);
                    resolve(gameScene); // Proceed anyway
                } else {
                    setTimeout(checkReady, 100);
                }
            };
            
            checkReady();
        });
    }
    
    /**
     * Validate game initialization was successful
     * @private
     */
    async _validateGameInitialization(gameSceneKey) {
        console.log('🔍 Validating game initialization...');
        
        const gameScene = window.game?.scene?.getScene(gameSceneKey);
        if (!gameScene) {
            throw new Error(`Game scene ${gameSceneKey} not found after initialization`);
        }
        
        // Check camera dimensions
        if (gameScene.cameras && gameScene.cameras.main) {
            const camera = gameScene.cameras.main;
            if (camera.width <= 0 || camera.height <= 0) {
                console.warn(`⚠️ Invalid camera dimensions: ${camera.width}x${camera.height}`);
                // Try to fix camera dimensions
                this._fixCameraDimensions(gameScene);
            } else {
                console.log(`✅ Valid camera dimensions: ${camera.width}x${camera.height}`);
            }
        }
        
        // Check if game container is properly visible
        const gameContainer = document.getElementById('game-container');
        const containerVisible = gameContainer && 
            getComputedStyle(gameContainer).display !== 'none' &&
            getComputedStyle(gameContainer).visibility !== 'hidden';
            
        if (!containerVisible) {
            console.warn('⚠️ Game container not properly visible after initialization');
            this._fixGameContainerVisibility();
        }
        
        console.log('✅ Game initialization validation complete');
    }
    
    /**
     * Fix camera dimensions if they're invalid
     * @private
     */
    _fixCameraDimensions(gameScene) {
        console.log('🔧 Fixing invalid camera dimensions...');
        
        const gameContainer = document.getElementById('game-container');
        if (gameContainer && gameScene.cameras && gameScene.cameras.main) {
            const containerRect = gameContainer.getBoundingClientRect();
            
            if (containerRect.width > 0 && containerRect.height > 0) {
                // Force camera to use container dimensions
                gameScene.cameras.main.setSize(containerRect.width, containerRect.height);
                console.log(`✅ Camera dimensions fixed: ${containerRect.width}x${containerRect.height}`);
                
                // Trigger resize to update all components
                if (gameScene.handleResize) {
                    gameScene.handleResize(containerRect.width, containerRect.height);
                }
            }
        }
    }
    
    /**
     * Fix game container visibility
     * @private
     */
    _fixGameContainerVisibility() {
        console.log('🔧 Fixing game container visibility...');
        
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            // Use proper PPT Hero positioning
            Object.assign(gameContainer.style, {
                display: 'block',
                visibility: 'visible',
                opacity: '1',
                position: 'absolute',
                top: '50px',
                left: '0',
                width: '100%',
                height: 'calc(100% - 50px)',
                zIndex: '1'
            });
            
            console.log('✅ Game container visibility fixed');
        }
    }
    
    /**
     * Restore DOM scene environment
     * @private
     */
    _restoreDOMSceneEnvironment() {
        console.log('🔄 Restoring DOM scene environment...');
        
        // Remove game active state
        document.body.classList.remove('game-active');
        
        // Hide game container
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            gameContainer.style.display = 'none';
            gameContainer.style.visibility = 'hidden';
        }
        
        // Hide top ribbon
        const topRibbon = document.getElementById('top-ribbon');
        if (topRibbon) {
            topRibbon.style.display = 'none';
        }
        
        console.log('✅ DOM scene environment restored');
    }
}

// Export singleton instance
export const gameInitializationCoordinator = GameInitializationCoordinator.getInstance();
export default gameInitializationCoordinator;
